import { Link, useLocation } from 'react-router-dom'
import { useAppUiI18n } from '@/i18n/AppUiI18nProvider'

type ShopQuayWordmarkProps = {
  to?: string
  /** light = dark teal mark on light bg; inverse = light mark on dark bg */
  variant?: 'light' | 'inverse'
  className?: string
  /** Height of the full icon+word lockup */
  heightClassName?: string
}

/**
 * Platform lockup: icon + wordmark.
 * English keeps the Syne SVG lockup; Chinese locales use 購運/购运 plus a lighter 網/网 suffix
 * so the lockup keeps a similar horizontal footprint to “ShopQuay”.
 */
export function ShopQuayWordmark({
  to = '/',
  variant = 'light',
  className = '',
  heightClassName = 'h-12 sm:h-14',
}: ShopQuayWordmarkProps) {
  const location = useLocation()
  const { locale, t } = useAppUiI18n()
  const isChinese = locale === 'zh-Hant' || locale === 'zh-CN'
  const brandName = t('brand.name')
  const webSuffix = t('brand.webSuffix')
  const alt = t('brand.lockupAlt')

  const scrollHomeIfSamePage = () => {
    if (location.pathname === to) {
      window.scrollTo(0, 0)
    }
  }

  if (!isChinese) {
    const src =
      variant === 'inverse'
        ? '/branding/shopquay-lockup-syne-on-dark.svg'
        : '/branding/shopquay-lockup-syne.svg'

    return (
      <Link
        to={to}
        onClick={scrollHomeIfSamePage}
        className={`inline-flex min-w-0 items-center ${className}`}
      >
        <img
          src={src}
          alt={alt}
          className={`w-auto max-w-[min(100%,22rem)] object-contain object-left ${heightClassName}`}
        />
      </Link>
    )
  }

  const iconSrc =
    variant === 'inverse'
      ? '/branding/shopquay-icon-on-dark.svg'
      : '/branding/shopquay-icon.svg'
  const nameClass =
    variant === 'inverse'
      ? 'text-[#e8f7f6]'
      : 'text-[#064152]'
  const suffixClass =
    variant === 'inverse'
      ? 'text-[#e8f7f6]/70'
      : 'text-[var(--mq-water,#146a6e)]'

  return (
    <Link
      to={to}
      onClick={scrollHomeIfSamePage}
      className={`inline-flex min-w-0 items-center gap-2 sm:gap-2.5 ${className}`}
      aria-label={alt}
    >
      <img
        src={iconSrc}
        alt=""
        aria-hidden
        className={`w-auto shrink-0 object-contain object-left ${heightClassName}`}
      />
      <span
        className={`inline-flex items-baseline gap-1 whitespace-nowrap leading-none ${nameClass}`}
        style={{
          fontFamily:
            '"Noto Sans TC", "Noto Sans SC", "PingFang TC", "PingFang SC", "Microsoft JhengHei", "Microsoft YaHei", sans-serif',
        }}
      >
        <span className="text-[1.9rem] font-semibold tracking-[0.1em] sm:text-[2.15rem]">
          {brandName}
        </span>
        {webSuffix ? (
          <span
            className={`translate-y-[-0.05em] text-[1.4rem] font-normal tracking-[0.2em] sm:text-[1.55rem] ${suffixClass}`}
          >
            {webSuffix}
          </span>
        ) : null}
      </span>
    </Link>
  )
}
